import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";

export type ScheduleCallTimeSlotOption = {
    value: string;
    label: string;
    disabled?: boolean;
};

export function buildScheduleCallTimeSlotOptions(): Array<ScheduleCallTimeSlotOption> {
    const slots: Array<ScheduleCallTimeSlotOption> = [];
    for (let hour = 9; hour <= 18; hour += 1) {
        for (const minute of [0, 30] as const) {
            if (hour === 18 && minute === 30) {
                break;
            }
            const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
            slots.push({value: value, label: value});
        }
    }
    return slots;
}

/** True when the slot's local start is strictly before `now` on the selected date (or today if none). */
export function isScheduleCallTimeSlotInThePast(args: {
    selectedDate: Date | null;
    timeSlot: string;
    now: Date;
}): boolean {
    const [rawHour, rawMinute] = args.timeSlot.split(":");
    const hour = Number.parseInt(rawHour ?? "", 10);
    const minute = Number.parseInt(rawMinute ?? "", 10);
    if (Number.isNaN(hour) === true || Number.isNaN(minute) === true) {
        return false;
    }

    const year = args.selectedDate === null ? args.now.getFullYear() : args.selectedDate.getFullYear();
    const month = args.selectedDate === null ? args.now.getMonth() : args.selectedDate.getMonth();
    const day = args.selectedDate === null ? args.now.getDate() : args.selectedDate.getDate();
    const slotInstant = new Date(year, month, day, hour, minute, 0, 0);
    return slotInstant.getTime() < args.now.getTime();
}

export function disablePastScheduleCallTimeSlots(args: {
    options: Array<ScheduleCallTimeSlotOption>;
    selectedDate: Date | null;
    now?: Date;
}): Array<ScheduleCallTimeSlotOption> {
    const now = args.now ?? new Date();
    const mapped: Array<ScheduleCallTimeSlotOption> = [];
    for (const option of args.options) {
        mapped.push({
            value: option.value,
            label: option.label,
            disabled: isScheduleCallTimeSlotInThePast({
                selectedDate: args.selectedDate,
                timeSlot: option.value,
                now: now,
            }),
        });
    }
    return mapped;
}

export function combineScheduleDateAndTimeSlot(dateValue: Date, timeSlot: string): Iso8601DateTime {
    const [rawHour, rawMinute] = timeSlot.split(":");
    const hour = Number.parseInt(rawHour ?? "0", 10);
    const minute = Number.parseInt(rawMinute ?? "0", 10);
    const merged = new Date(dateValue);
    merged.setHours(Number.isNaN(hour) ? 0 : hour, Number.isNaN(minute) ? 0 : minute, 0, 0);
    return merged.toISOString() as Iso8601DateTime;
}

export function journeyEvents(journey: EventContainerWithEvents): EventContainerWithEvents["events"] {
    return Array.isArray(journey.events) ? journey.events : [];
}

export function countJourneyEventsByTemplate(journey: EventContainerWithEvents, eventTemplateId: Uuid): number {
    return journeyEvents(journey).filter((event) => event.templateId === eventTemplateId).length;
}

export function parseScheduleNumericColumn(raw: unknown): number {
    if (typeof raw === "number" && Number.isNaN(raw) === false) {
        return raw;
    }
    if (typeof raw === "string" && raw.trim() !== "") {
        const parsed = Number.parseInt(raw, 10);
        if (Number.isNaN(parsed) === false) {
            return parsed;
        }
    }
    return 0;
}

export function copyJourneyColumnToEventWhenPresent(
    eventColumns: Record<string, unknown>,
    journeyValues: Record<string, unknown>,
    journeyColumnId: string,
    eventColumnId: string,
): void {
    const raw = journeyValues[journeyColumnId];
    if (raw === undefined || raw === null || String(raw).trim() === "") {
        return;
    }
    eventColumns[eventColumnId] = raw;
}

/** @deprecated Use `buildScheduleCallTimeSlotOptions` */
export const buildListScheduleCallTimeSlotOptions = buildScheduleCallTimeSlotOptions;

/** @deprecated Use `combineScheduleDateAndTimeSlot` */
export const combineListScheduleDateAndTimeSlot = combineScheduleDateAndTimeSlot;

/** @deprecated Use `parseScheduleNumericColumn` */
export const parseListScheduleNumericColumn = parseScheduleNumericColumn;
